import * as chrono from 'chrono-node';
import { parseEstimate } from './estimate.ts';
import { parseRecurrence } from './recurrence.ts';
import type { ParsedRecurrence } from './recurrence.ts';
import { tokenize } from './tokenizer.ts';
import type { OpToken } from './tokenizer.ts';

export type Energy = 'low' | 'med' | 'high';
export type Daypart = 'morning' | 'afternoon' | 'evening' | 'night';
export type Priority = 0 | 1 | 2 | 3;

export interface ParsedTask {
  title: string;
  tags: string[];
  priority?: Priority;
  dueAt?: number;
  dueAtHasTime?: boolean;
  scheduledAt?: number;
  scheduledAtHasTime?: boolean;
  estimateMinutes?: number;
  recurrence?: ParsedRecurrence;
  location?: string;
  owner?: string;
  notes?: string;
  energy?: Energy;
  daypart?: Daypart;
  projectName?: string;
}

export interface ParseEmpty {
  error: 'empty';
  title?: undefined;
  tags?: undefined;
  priority?: undefined;
  dueAt?: undefined;
  dueAtHasTime?: undefined;
  scheduledAt?: undefined;
  scheduledAtHasTime?: undefined;
  estimateMinutes?: undefined;
  recurrence?: undefined;
  location?: undefined;
  owner?: undefined;
  notes?: undefined;
  energy?: undefined;
  daypart?: undefined;
  projectName?: undefined;
}

export interface ParseSuccess extends ParsedTask {
  error?: undefined;
}

export type ParseResult = ParseSuccess | ParseEmpty;

export interface ParseOptions {
  /** Reference time for natural-language date parsing. Defaults to "now". */
  now?: Date;
}

const ENERGIES: ReadonlySet<Energy> = new Set(['low', 'med', 'high']);
const DAYPARTS: ReadonlySet<Daypart> = new Set(['morning', 'afternoon', 'evening', 'night']);

function parsePriority(value: string): Priority | undefined {
  if (value === '0') return 0;
  if (value === '1') return 1;
  if (value === '2') return 2;
  if (value === '3') return 3;
  return undefined;
}

interface ParsedDateToken {
  at: number;
  hasTime: boolean;
}

function parseDate(value: string, now: Date): ParsedDateToken | undefined {
  const result = chrono.parse(value, now)[0];
  if (!result) return undefined;

  const hasTime = result.start.isCertain('hour') || result.start.isCertain('minute');
  const date = result.start.date();
  if (!hasTime) date.setHours(0, 0, 0, 0);

  return { at: date.getTime(), hasTime };
}

export function parseSmartAdd(input: string, options: ParseOptions = {}): ParseResult {
  const now = options.now ?? new Date();
  const tokens = tokenize(input);

  // Title is the leading text token, if any.
  const titleToken = tokens.find((t) => t.type === 'text');
  if (!titleToken || !titleToken.value) {
    return { error: 'empty' };
  }
  const title = titleToken.value;

  const result: ParseSuccess = { title, tags: [] };
  const seenTags = new Set<string>();

  for (const token of tokens) {
    if (token.type !== 'op') continue;
    const op = token as OpToken;
    switch (op.operator) {
      case '!': {
        const p = parsePriority(op.value);
        if (p !== undefined) result.priority = p;
        break;
      }
      case '#': {
        if (!seenTags.has(op.value)) {
          seenTags.add(op.value);
          result.tags.push(op.value);
        }
        break;
      }
      case '=': {
        const m = parseEstimate(op.value);
        if (m !== undefined) result.estimateMinutes = m;
        break;
      }
      case '/': {
        result.location = op.value;
        break;
      }
      case '@': {
        const v = op.value.toLowerCase() as Energy;
        if (ENERGIES.has(v)) result.energy = v;
        else result.location = op.value;
        break;
      }
      case '+': {
        result.owner = op.value;
        break;
      }
      case '//': {
        result.notes = op.value;
        break;
      }
      case '%': {
        const v = op.value.toLowerCase() as Daypart;
        if (DAYPARTS.has(v)) result.daypart = v;
        break;
      }
      case '>': {
        result.projectName = op.value;
        break;
      }
      case '*': {
        const r = parseRecurrence(op.value);
        if (r) result.recurrence = r;
        break;
      }
      case '^': {
        const t = parseDate(op.value, now);
        if (t !== undefined) {
          result.dueAt = t.at;
          result.dueAtHasTime = t.hasTime;
        }
        break;
      }
      case '~': {
        const t = parseDate(op.value, now);
        if (t !== undefined) {
          result.scheduledAt = t.at;
          result.scheduledAtHasTime = t.hasTime;
        }
        break;
      }
    }
  }

  return result;
}
